import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { AbstractControl, FormControl, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';

import {
  Dropdown,
  DropdownMenus,
} from 'src/app/services/dropdowns/dropdowns.model';
import { DropdownsService } from 'src/app/services/dropdowns/dropdowns.service';

/**
 * Contains the following {FormControl}s:
 * * CheckingAccountStatus *
 * * CheckingBankName
 * * HasDebitCard
 *
 * (*) Denotes a control that can be disabled.
 *
 * The parent {FormGroup} can disable unneeded controls. Disabling will hide the controls from the UI.
 *
 * ```typescript
 * this.formBuilder.group({
 *    CheckingAccountStatus: [{ value: '', disabled: true }]
 * });
 * ```
 */
@Component({
  selector: 'app-banking-info',
  templateUrl: './banking-info.component.html',
  styleUrls: ['./banking-info.component.scss'],
})
export class BankingInfoComponent implements OnInit, OnDestroy {
  @Output() applicantChangeEvent = new EventEmitter<string>();
  @Output() coApplicantChangeEvent = new EventEmitter<string>();

  @Input() controls: {
    [key: string]: AbstractControl;
  } = {};

  /**
   * If true, the {FormControl}s will be split into two columns.
   */
  @Input() split = false;

  /**
   * Determines if the {FormControl}s are for the co-applicant and sends the
   * change event to the parent component.
   */
  @Input() coApplicant = false;

  /**
   * Number of times these {FormControl}s have been used on same page. Defaults to `1`.
   */
  @Input() count = 1;

  dropdownMenus: DropdownMenus | null = null;
  checkingAccountStatus: Array<Dropdown> = [];
  debitCard: Array<Dropdown> = [];

  private subscriptions: Array<Subscription> = [];

  constructor(private dropdownsService: DropdownsService) {}

  ngOnInit(): void {
    this.subscriptions.push(
      this.dropdownsService.dropdownMenus.subscribe(
        (data: DropdownMenus | null) => {
          if (data) {
            this.dropdownMenus = { ...data };
            this.checkingAccountStatus = [
              ...this.dropdownMenus.checkingOptions,
            ];
            this.debitCard = [...this.dropdownMenus.debitOptions];
          }
        }
      )
    );
  }

  ngOnDestroy(): void {
    if (this.subscriptions.length > 0) {
      this.subscriptions.forEach((sub) => {
        sub.unsubscribe();
      });
    }
  }

  getFormControl(key: string): FormControl {
    return this.controls[key] as FormControl;
  }

  controlIsRequired(key: string): boolean {
    if (
      this.controls[key] &&
      this.controls[key].hasValidator(Validators.required)
    ) {
      return true;
    }

    return false;
  }

  controlIsDisabled(key: string): boolean {
    if (this.controls[key]) {
      return this.controls[key].disabled;
    }

    return false;
  }

  /**
   * Emits the event to the parent component.
   *
   * @param event The event to emit to the parent component.
   * @param coApplicant Determines if the event is for the co-applicant.
   */
  onChangeEvent(event: string, coApplicant = false) {
    if (coApplicant) {
      this.coApplicantChangeEvent.emit(event);
      return;
    }

    this.applicantChangeEvent.emit(event);
  }
}
